import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { 
  ArrowLeft,
  ArrowRight,
  Zap,
  TrendingUp,
  Shield,
  Users,
  Target,
  Sparkles,
  Globe,
  Crown,
  ExternalLink,
  MessageSquare,
  BarChart3,
  Clock,
  Heart
} from "lucide-react";

const LOGO_URL = "/doslions-logo.png";

export default function About() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate("/")}
                className="text-zinc-400 hover:text-amber-500"
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div 
                className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() => navigate("/")}
                data-testid="logo-home-link"
              >
                <img src={LOGO_URL} alt="ECOLIONS" className="w-10 h-10 object-contain" />
                <div>
                  <span className="font-bold text-xl tracking-tight text-amber-500" style={{ fontFamily: 'Barlow Condensed' }}>
                    ECOLIONS
                  </span>
                  <span className="font-bold text-xl tracking-tight text-white ml-1" style={{ fontFamily: 'Barlow Condensed' }}>
                    AI MARKETS
                  </span>
                </div>
              </div>
            </div>
            <Button 
              onClick={() => navigate("/auth")}
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider"
              data-testid="get-started-btn"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(245,158,11,0.15),transparent_50%)]"></div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(16,185,129,0.1),transparent_50%)]"></div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 mb-8">
            <Heart className="w-4 h-4 text-amber-500" />
            <span className="text-sm tracking-wider text-amber-500 font-bold uppercase">
              Our Mission
            </span>
          </div>
          
          <h1 
            className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter uppercase mb-6"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            About <span className="text-amber-500">ECO</span>LIONS
          </h1>
          
          <p className="text-xl text-zinc-300 max-w-3xl mx-auto mb-8" style={{ fontFamily: 'Manrope' }}>
            Empowering <span className="text-amber-500 font-bold">Ecopreneurs</span> worldwide to make 
            smarter decisions with the power of <span className="text-emerald-500 font-bold">Artificial Intelligence</span>.
          </p>
        </div>
      </section>

      {/* The Vision Section */}
      <section className="py-20 px-4 border-t border-zinc-900">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-amber-500/20 flex items-center justify-center">
              <Target className="w-6 h-6 text-amber-500" />
            </div>
            <h2 
              className="text-3xl font-bold tracking-tight uppercase"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              The <span className="text-amber-500">Vision</span>
            </h2>
          </div>
          
          <div className="bg-[#111111] border border-zinc-800 p-8 space-y-6">
            <p className="text-lg text-zinc-300 leading-relaxed">
              In a world where <span className="text-amber-500 font-bold">information is power</span>, we believe everyone 
              deserves access to intelligent tools that were once reserved for Wall Street elites.
            </p>
            
            <p className="text-lg text-zinc-300 leading-relaxed">
              <span className="text-amber-500 font-bold">ECOLIONS AI Markets</span> was born from a simple idea: 
              <span className="italic"> "What if every entrepreneur, every dreamer, every ecopreneur could have 
              their own AI assistant that understands markets, trends, and opportunities?"</span>
            </p>
            
            <p className="text-lg text-zinc-300 leading-relaxed">
              We're not just another data platform. We're a <span className="text-emerald-500 font-bold">movement</span>. 
              A community of forward-thinkers who understand that <span className="text-amber-500 font-bold">AI is the future</span>, 
              and the future is NOW.
            </p>
          </div>
        </div>
      </section>

      {/* Why ECOLIONS Section */}
      <section className="py-20 px-4 border-t border-zinc-900 bg-gradient-to-b from-transparent via-amber-500/5 to-transparent">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 
              className="text-4xl font-bold tracking-tight uppercase mb-4"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              Why <span className="text-amber-500">ECO</span>LIONS?
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Because the future belongs to those who embrace innovation.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-[#111111] border border-amber-500/30 p-6 text-center">
              <div className="w-16 h-16 bg-amber-500/20 mx-auto flex items-center justify-center mb-4">
                <Zap className="w-8 h-8 text-amber-500" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-amber-500" style={{ fontFamily: 'Barlow Condensed' }}>
                AI-POWERED INSIGHTS
              </h3>
              <p className="text-sm text-zinc-400">
                Our LIONAI assistant analyzes markets 24/7, giving you insights that save hours of research.
                The same technology used by major institutions, now in YOUR hands.
              </p>
            </div>

            <div className="bg-[#111111] border border-emerald-500/30 p-6 text-center">
              <div className="w-16 h-16 bg-emerald-500/20 mx-auto flex items-center justify-center mb-4">
                <Globe className="w-8 h-8 text-emerald-500" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-emerald-500" style={{ fontFamily: 'Barlow Condensed' }}>
                GLOBAL COVERAGE
              </h3>
              <p className="text-sm text-zinc-400">
                Stocks, commodities, crypto - we track it all. Real-time data from markets around the world.
                Stay informed, stay ahead.
              </p>
            </div>

            <div className="bg-[#111111] border border-purple-500/30 p-6 text-center">
              <div className="w-16 h-16 bg-purple-500/20 mx-auto flex items-center justify-center mb-4">
                <Users className="w-8 h-8 text-purple-500" />
              </div>
              <h3 className="text-xl font-bold mb-3 text-purple-500" style={{ fontFamily: 'Barlow Condensed' }}>
                ECOPRENEUR COMMUNITY
              </h3>
              <p className="text-sm text-zinc-400">
                Join 500+ ecopreneurs who are already using AI to enhance their decision-making.
                You're not just a user, you're part of a movement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Meet LIONAI Section */}
      <section className="py-20 px-4 border-t border-zinc-900">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="w-32 h-32 bg-amber-500/20 flex items-center justify-center flex-shrink-0">
              <img src={LOGO_URL} alt="LIONAI" className="w-20 h-20 object-contain" />
            </div>
            <div>
              <h2 
                className="text-3xl font-bold tracking-tight uppercase mb-4"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                Meet <span className="text-amber-500">LION</span>AI
              </h2>
              <p className="text-lg text-zinc-300 mb-4">
                Your personal AI analyst. Ask anything about markets, get instant insights. 
                LIONAI is powered by <span className="text-purple-400 font-bold">advanced AI</span> technology - 
                cutting-edge artificial intelligence that's transforming how ecopreneurs analyze markets.
              </p>
              <p className="text-zinc-400">
                With LIONAI, you're not just getting data. You're getting <span className="text-amber-500">intelligence</span>. 
                Personalized insights tailored to YOUR interests, YOUR watchlist, YOUR goals.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* DOSLIONS Section */}
      <section className="py-20 px-4 border-t border-zinc-900">
        <div className="max-w-4xl mx-auto text-center">
          <a 
            href="https://www.doslions.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block group"
          >
            <img 
              src={LOGO_URL} 
              alt="DOSLIONS" 
              className="w-24 h-24 object-contain mx-auto mb-4 group-hover:scale-105 transition-transform"
            />
            <h2 
              className="text-3xl font-bold tracking-tight uppercase mb-2"
              style={{ fontFamily: 'Barlow Condensed' }}
            >
              A <span className="text-amber-500">DOSLIONS</span> Project
            </h2>
            <p className="text-zinc-400 mb-4 flex items-center justify-center gap-2">
              Visit doslions.com <ExternalLink className="w-4 h-4" />
            </p>
          </a>
          <p className="text-zinc-500 max-w-2xl mx-auto">
            ECOLIONS AI Markets is proudly developed by <span className="text-amber-500 font-bold">DOSLIONS</span> - 
            a creative studio dedicated to ART, BRAND, and the ECOPRENEUR spirit. 
            We build tools that empower creators and entrepreneurs to thrive in the digital age.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 border-t border-zinc-900 bg-gradient-to-b from-transparent to-amber-500/10">
        <div className="max-w-3xl mx-auto text-center">
          <h2 
            className="text-4xl sm:text-5xl font-bold tracking-tight mb-6 uppercase"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            Ready to <span className="text-amber-500">ROAR</span>?
          </h2>
          <p className="text-zinc-400 mb-8 text-lg">
            Join the ECOLIONS community today. Start free, upgrade when you're ready.
            The future of AI-powered insights is waiting for you.
          </p>
          <Button 
            onClick={() => navigate("/auth")}
            className="bg-amber-500 hover:bg-amber-600 text-black font-bold uppercase tracking-wider px-10 py-6 text-lg shadow-[0_0_30px_rgba(245,158,11,0.4)]"
            data-testid="cta-btn"
          >
            <Crown className="w-5 h-5 mr-2" />
            Join ECOLIONS Now <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Footer with Disclaimer */}
      <footer className="py-12 px-4 border-t border-zinc-900 bg-[#050505]">
        <div className="max-w-6xl mx-auto">
          {/* Main Footer */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="ECOLIONS" className="w-10 h-10 object-contain" />
              <span className="font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                <span className="text-amber-500">ECOLIONS</span> AI MARKETS
              </span>
            </div>
            <div className="flex items-center gap-4 text-sm text-zinc-500">
              <a href="https://www.doslions.com" target="_blank" rel="noopener noreferrer" className="hover:text-amber-500 flex items-center gap-1">
                doslions.com <ExternalLink className="w-3 h-3" />
              </a>
              <span>|</span>
              <a href="mailto:doslionsteam11@gmail.com" className="hover:text-amber-500">Contact</a>
            </div>
          </div>

          {/* Legal Disclaimer - Small Footer Section */}
          <div className="border-t border-zinc-800 pt-8">
            <p className="text-xs text-zinc-600 text-center leading-relaxed max-w-4xl mx-auto">
              <span className="text-zinc-500 font-semibold">DISCLAIMER:</span> ECOLIONS AI Markets is an 
              <span className="text-amber-600"> educational and experimental project</span> developed by 
              <span className="text-amber-600"> DOSLIONS</span> to help Ecopreneurs make better decisions 
              using AI technology. This platform is <span className="text-amber-600">NOT a financial company</span>, 
              investment advisor, or broker. All market data and AI-generated insights are provided for 
              <span className="text-amber-600"> informational and educational purposes only</span>.
            </p>
            <p className="text-xs text-zinc-600 text-center leading-relaxed max-w-4xl mx-auto mt-3">
              Our AI assistant is powered by advanced AI technology. 
              Past performance does not guarantee future results. Always do your own research before making any 
              financial decisions. ECOLIONS, DOSLIONS, and its creators are not responsible for any losses, 
              damages, or outcomes resulting from the use of this platform or its information.
            </p>
            <p className="text-xs text-zinc-700 text-center mt-4">
              © 2026 ECOLIONS AI Markets | A DOSLIONS Project | All Rights Reserved
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
